import React from 'react';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import type { Color, RGBA } from '../../types/layout';
import type { StyleColors } from '../../types/styles';
import {
  getMediaQueryHeader,
  isColorReference,
  resolveColorReference,
  checkIsGradient,
  rgbaToString,
  hexToRgba,
} from '../../utils/styleHelpers';
import { css, cx } from '@emotion/css';
import {
  SHAPE_DIVIDER_DEFAULTS,
  SHAPE_DIVIDER_VIEWBOX,
  getGradientEdgeColor,
  getShapeDividerPath,
  type ShapeDividerType,
} from './shapes';

export type ShapeDividerProps = {
  type: ShapeDividerType;
  position: 'top' | 'bottom';
  height: number;
  frequency: number;
  flip: boolean;
  curveCenter: number;
  color?: Color | string;
  styleColors: StyleColors;
  isServerRender: boolean;
  mobileHeight?: number;
  tabletHeight?: number;
  nodeId: string;
};

function resolveShapeDividerFill(
  color: Color | string | undefined,
  styleColors: StyleColors,
  uniqueId: string,
): { fill: string; gradientDef: React.ReactNode | null } {
  if (!color) return { fill: 'currentColor', gradientDef: null };

  let resolved: Color | undefined;
  if (isColorReference(color)) {
    resolved = resolveColorReference(color as string, styleColors);
  } else if (typeof color !== 'string') {
    resolved = color;
  }

  if (!resolved) return { fill: 'currentColor', gradientDef: null };

  if (checkIsGradient(resolved)) {
    const gradientId = `shape-divider-gradient-${uniqueId}`;
    if (resolved.type === 'Linear') {
      const angle = resolved.angle || 0;
      const rad = ((angle - 90) * Math.PI) / 180;
      const x1 = 50 - 50 * Math.cos(rad);
      const y1 = 50 - 50 * Math.sin(rad);
      const x2 = 50 + 50 * Math.cos(rad);
      const y2 = 50 + 50 * Math.sin(rad);
      return {
        fill: `url(#${gradientId})`,
        gradientDef: (
          <defs>
            <linearGradient id={gradientId} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>
              {resolved.stops.map((stop, i) => (
                <stop key={i} offset={`${stop.position * 100}%`} stopColor={stop.hexa} />
              ))}
            </linearGradient>
          </defs>
        ),
      };
    }
    if (resolved.type === 'Radial') {
      const cx = resolved.centerPoint?.x ?? 50;
      const cy = resolved.centerPoint?.y ?? 50;
      return {
        fill: `url(#${gradientId})`,
        gradientDef: (
          <defs>
            <radialGradient id={gradientId} cx={`${cx}%`} cy={`${cy}%`} r="50%">
              {resolved.stops.map((stop, i) => (
                <stop key={i} offset={`${stop.position * 100}%`} stopColor={stop.hexa} />
              ))}
            </radialGradient>
          </defs>
        ),
      };
    }
  }

  return { fill: rgbaToString(resolved as RGBA), gradientDef: null };
}

export const ShapeDivider: React.FC<ShapeDividerProps> = ({
  type,
  position,
  height,
  frequency,
  flip,
  curveCenter,
  color,
  styleColors,
  isServerRender,
  mobileHeight,
  tabletHeight,
  nodeId,
}) => {
  const path = getShapeDividerPath(type, frequency, curveCenter);

  const { fill, gradientDef } = resolveShapeDividerFill(color, styleColors, nodeId);

  const isTop = position === 'top';

  const wrapperStyle = css({
    position: 'absolute',
    left: 0,
    width: '100%',
    height: `${height}px`,
    pointerEvents: 'none',
    lineHeight: 0,
    ...(isTop
      ? { top: 0, ...(flip ? { transform: 'scaleX(-1)' } : {}) }
      : { bottom: 0, transform: flip ? 'scaleX(-1) scaleY(-1)' : 'scaleY(-1)' }),
    ...(tabletHeight
      ? {
          [getMediaQueryHeader(isServerRender, 'tablet')]: {
            height: `${tabletHeight}px`,
          },
        }
      : {}),
    ...(mobileHeight
      ? {
          [getMediaQueryHeader(isServerRender, 'mobile')]: {
            height: `${mobileHeight}px`,
          },
        }
      : {}),
  });

  return (
    <div className={cx('shape-divider', wrapperStyle)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={SHAPE_DIVIDER_VIEWBOX}
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        {gradientDef}
        <path d={path} fill={fill} />
      </svg>
    </div>
  );
};

/**
 * Resolves the auto color for a shape divider by looking at the adjacent
 * container's background color. If that color is a gradient, returns the
 * stop closest to the divider (the near edge of the adjacent container).
 * Falls back to the page background, then to global color 6.
 */
export function resolveAutoShapeDividerColor(
  nodeId: string,
  position: 'top' | 'bottom',
  query: ReturnType<typeof useEditor>['query'],
  styleColors: StyleColors,
): Color | string {
  const DEFAULT_FALLBACK = '$ref:#/colors/color6';
  try {
    const currentNode = query.node(nodeId).get();
    if (!currentNode) return DEFAULT_FALLBACK;

    const parentId = currentNode.data.parent as string | undefined;
    if (!parentId) return DEFAULT_FALLBACK;

    const parentNode = query.node(parentId).get();
    if (!parentNode) return DEFAULT_FALLBACK;

    const siblings = parentNode.data.nodes as string[];
    const currentIndex = siblings.indexOf(nodeId);

    // For bottom divider, look at the next sibling; for top, look at the previous sibling
    const adjacentIndex = position === 'bottom' ? currentIndex + 1 : currentIndex - 1;

    if (adjacentIndex >= 0 && adjacentIndex < siblings.length) {
      const adjacentNode = query.node(siblings[adjacentIndex]).get();
      const adjacentBgColor = adjacentNode?.data.props?.backgroundColor as
        | Color
        | string
        | undefined;
      if (adjacentBgColor) {
        // The adjacent container's edge closest to the divider:
        // bottom divider → top edge of container below
        // top divider → bottom edge of container above
        const nearEdge = position === 'bottom' ? 'top' : 'bottom';
        return extractEdgeColor(adjacentBgColor, nearEdge, styleColors);
      }
    }

    // Fall back to page (root) background color
    const rootNode = query.node(ROOT_NODE).get();
    const pageBgColor = rootNode?.data.props?.backgroundColor as Color | string | undefined;
    if (pageBgColor) {
      return extractEdgeColor(pageBgColor, 'top', styleColors);
    }
  } catch {
    // If any query fails, fall back to default
  }

  return DEFAULT_FALLBACK;
}

/**
 * If the color resolves to a gradient, extracts the stop color closest to
 * the given edge. Otherwise returns the color as-is.
 */
function extractEdgeColor(
  color: Color | string,
  edge: 'top' | 'bottom',
  styleColors: StyleColors,
): Color | string {
  let resolved: Color | undefined;

  if (isColorReference(color)) {
    resolved = resolveColorReference(color as string, styleColors);
  } else if (typeof color !== 'string') {
    resolved = color;
  }

  if (resolved && checkIsGradient(resolved)) {
    const hex = getGradientEdgeColor(resolved, edge);
    const rgba = hexToRgba(hex);
    if (rgba) return rgba;
  }

  // Solid color or unresolvable — return original value
  return color;
}

export { SHAPE_DIVIDER_DEFAULTS };
export type { ShapeDividerType };
